import { Spacing } from '@vkontakte/vkui';
import React, { type VFC, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { autoUploadUnsubs } from 'reactApp/appHelpers/featuresHelpers/features/autoUploadPromo';
import { updatedUnsubScreensEnabled } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewUnsub';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import type { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { SPACE_SIZES } from 'reactApp/modules/products/products.helpers';
import {
    ESubscriptionModalAction,
    useSendSubscriptionModalAnalytics,
} from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import type { RootState } from 'reactApp/store';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { formatDefaultDate, formatLocale } from 'reactApp/utils/formatDate';

import styles from './CancelAutoRenewalModal.css';
import type { DialogProps } from './CancelAutoRenewalModal.types';
import { useSendAnalyticAfterOpen } from './hooks/useSendAnalyticsAfterOpen';
import { PrivilegeList } from './PrivilegeList/PrivilegeList';

type Props = IPropsWithPopup<DialogProps>;

export const CancelAutoRenewalModal: VFC<Props> = ({ currentSpace, expires, id, dwhData, onClose, onAction, onSuccess }) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    useSendAnalyticAfterOpen(dwhData);
    const subscription = useSelector((state: RootState) =>
        BillingDataSelectors.getSubscriptionsById(state, id)
    ) as BillingDataSubscriptionInfo;

    const { isTrial, disableAds } = subscription;

    const sendAnalytics = useSendSubscriptionModalAnalytics(subscription);

    useEffect(() => {
        emitAnalyticEvent(AnalyticEventNames.MY_TARIFF_CANCEL_AUTOPAY_SHOW, { isTrial });
        sendAnalytics(ESubscriptionModalAction.showCancelAutopayment);
    }, [isTrial]);

    const showAutouploadVariant = autoUploadUnsubs && !subscription?.isTrial && subscription?.space?.original >= SPACE_SIZES._256;

    const getSubtitle = useCallback(() => {
        if (!expires) {
            return '';
        }

        if (updatedUnsubScreensEnabled) {
            return `С ${formatLocale(expires, 'd MMMM yyyy')} пропадут:`;
        } else if (showAutouploadVariant) {
            return `С ${formatLocale(expires, 'd MMMM yyyy')} у вас:`;
        }

        return `С ${formatDefaultDate(expires)} вы потеряете:`;
    }, []);

    return (
        <MobileDialog
            id="mobile-cancel-auto-renewal-modal"
            onClose={onClose}
            closeOnDimmerClick
            mod="confirm"
            topmost
            isRebrandingQuotaLanding
            content16PaddingOnly
            title={
                <span className={styles.title}>
                    {showAutouploadVariant ? (
                        'Отключить подписку?'
                    ) : (
                        <>
                            Отключить
                            <br />
                            автопродление?
                        </>
                    )}
                </span>
            }
            scrollRef={scrollRef}
            footer={
                <>
                    <Spacing size={8} />
                    <Button primary onClick={onSuccess} theme="vk" sizeMode={ButtonSizeMode.big} className={styles.button}>
                        Сохранить {showAutouploadVariant ? 'подписку' : 'автоплатёж'}
                    </Button>
                    <Spacing size={12} />
                    <Button secondary onClick={onAction} theme="vk" sizeMode={ButtonSizeMode.big} className={styles.button}>
                        Отключить
                    </Button>
                </>
            }
        >
            {expires && <span className={styles.subtitle}>{getSubtitle()}</span>}
            {/* tempexp_16460-next-line */}
            <PrivilegeList
                ref={scrollRef}
                currentSpace={currentSpace}
                productId={subscription.productId}
                isAutoUpload={showAutouploadVariant}
                showAds={!disableAds}
            />
        </MobileDialog>
    );
};
